import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import { subMonths, format } from 'date-fns';
import { fetchXeroReport, type XeroArReport } from '../../services/xeroApi';
import { fmtXeroMoney, XeroHero, XeroStatCard } from '../../components/xero/xeroShared';

const XeroReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<XeroArReport | null>(null);
  const [from, setFrom] = useState(format(subMonths(new Date(), 12), 'yyyy-MM-dd'));
  const [to, setTo] = useState(format(new Date(), 'yyyy-MM-dd'));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetchXeroReport({ from, to });
      setReport(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  const agingRows = report
    ? [
        { label: 'Current (not yet due)', value: report.aging.current },
        { label: '1–30 days overdue', value: report.aging.d1_30 },
        { label: '31–60 days overdue', value: report.aging.d31_60 },
        { label: '61–90 days overdue', value: report.aging.d61_90 },
        { label: '90+ days overdue', value: report.aging.d90plus },
      ]
    : [];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
        <XeroHero
          compact
          title="Xero · Reports"
          subtitle="Accounts receivable aging and overdue summary for follow-up."
        />
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => void load()} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
        <TextField
          label="From"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
        <Button variant="contained" onClick={() => void load()} disabled={loading}>
          Apply
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {report && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <XeroStatCard
                icon={<WarningAmberOutlinedIcon />}
                label="Overdue AR"
                value={fmtXeroMoney(report.totalOverdue)}
                sub={`${report.overdueCount} invoice(s)`}
                color="#ef4444"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <XeroStatCard
                icon={<ReceiptLongOutlinedIcon />}
                label="Total outstanding"
                value={fmtXeroMoney(report.totalOutstanding)}
                sub={`${report.unpaidCount} unpaid`}
                color="#f59e0b"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <XeroStatCard
                icon={<CheckCircleOutlineIcon />}
                label="Paid this month"
                value={fmtXeroMoney(report.paidThisMonthTotal)}
                sub={`${report.paidThisMonthCount} invoice(s)`}
                color="#22c55e"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: 1,
                }}
              >
                <Button variant="contained" color="error" onClick={() => navigate('/xero/invoices?filter=overdue')}>
                  View overdue invoices
                </Button>
                <Button variant="outlined" onClick={() => navigate('/xero/invoices?filter=unpaid')}>
                  All unpaid
                </Button>
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    AR aging
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Bucket</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {agingRows.map((row) => (
                        <TableRow key={row.label}>
                          <TableCell>{row.label}</TableCell>
                          <TableCell align="right">{fmtXeroMoney(row.value)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    Top debtors (overdue)
                  </Typography>
                  {report.topDebtors.length === 0 ? (
                    <Typography color="text.secondary" py={2}>
                      No overdue balances — nice work.
                    </Typography>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Customer</TableCell>
                          <TableCell align="right">Overdue</TableCell>
                          <TableCell align="right">Invoices</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {report.topDebtors.map((d) => (
                          <TableRow
                            key={d.name}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => navigate(`/xero/invoices?filter=overdue&search=${encodeURIComponent(d.name)}`)}
                          >
                            <TableCell>{d.name}</TableCell>
                            <TableCell align="right">{fmtXeroMoney(d.overdue)}</TableCell>
                            <TableCell align="right">{d.count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default XeroReportsPage;
